import type { Arcade } from "@arcadeai/arcadejs";
import type { ToolDefinition } from "@arcadeai/arcadejs/resources/index.mjs";
import { findToolkitByName } from "@arcadeai/design-system/metadata/utils";
import type {
  ProcessedTool,
  ProcessedToolkit,
  Toolkit,
  ToolkitCollection,
} from "./arcade";

// Regex patterns for normalizing text
const DELIMITER_REGEX = /[._\-\s]+/;
const CAMEL_CASE_REGEX = /([a-z])([A-Z])/g;
const WHITESPACE_REGEX = /\s+/;

/**
 * Helper function to create normalized searchable text from multiple strings.
 * Separates compound terms (like Gmail.List_emails) into individual searchable components.
 * Handles camelCase, snake_case, kebab-case, and dot.notation.
 */
export const normalizeText = (parts: string[]) => {
  const termSet = new Set<string>();

  for (const part of parts) {
    if (!part) {
      continue;
    }

    const lowerPart = part.toLowerCase();
    termSet.add(lowerPart);

    const segments = part.split(DELIMITER_REGEX);
    for (const segment of segments) {
      if (segment.length <= 1) {
        continue;
      }

      const camelCaseSplit = segment.replace(CAMEL_CASE_REGEX, "$1 $2");
      const subTerms = camelCaseSplit.split(WHITESPACE_REGEX);

      for (const term of subTerms) {
        if (term.length > 1) {
          termSet.add(term.toLowerCase());
        }
      }
    }
  }

  return Array.from(termSet).join(" ");
};

const mergeSecrets = (
  newSecrets: Arcade.Tools.ToolDefinition.Requirements.Secret[] = [],
  existingSecrets: Arcade.Tools.ToolDefinition.Requirements.Secret[] = []
) => {
  if (newSecrets.length === 0) {
    return existingSecrets;
  }

  const m = new Map<string, Arcade.Tools.ToolDefinition.Requirements.Secret>();
  for (const s of existingSecrets) {
    m.set(s.key, { ...s });
  }

  for (const s of newSecrets) {
    const prev = m.get(s.key);
    if (!prev) {
      m.set(s.key, { ...s });
      continue;
    }

    const met = !!(prev.met || s.met);
    const status_reason = s.status_reason ?? prev.status_reason;
    m.set(s.key, { key: s.key, met, status_reason });
  }

  return Array.from(m.values());
};

const mergeAuthorizations = (
  newAuth: Arcade.Tools.ToolDefinition.Requirements.Authorization | undefined,
  existing: Arcade.Tools.ToolDefinition.Requirements.Authorization[] = []
) => {
  if (!newAuth?.provider_id) {
    return existing;
  }

  const m = new Map(existing.map((a) => [a.provider_id, { ...a }]));
  const existingAuth = m.get(newAuth.provider_id);

  if (!existingAuth) {
    m.set(newAuth.provider_id, { ...newAuth });
    return Array.from(m.values());
  }

  const existingScopes = new Set(existingAuth.oauth2?.scopes ?? []);
  for (const s of newAuth.oauth2?.scopes ?? []) {
    existingScopes.add(s);
  }

  m.set(newAuth.provider_id, {
    ...existingAuth,
    oauth2: {
      ...(existingAuth.oauth2 ?? {}),
      scopes: Array.from(existingScopes),
    },
  });

  return Array.from(m.values());
};

const mergeScopes = (
  providerId: string | undefined,
  newScopes: string[] = [],
  existingScopes: Record<string, string[]> = {}
) => {
  if (!providerId || newScopes.length === 0) {
    return existingScopes;
  }
  const set = new Set(existingScopes[providerId] ?? []);
  for (const s of newScopes) {
    set.add(s);
  }
  return { ...existingScopes, [providerId]: Array.from(set) };
};

const applyToolRequirements = (
  toolkit: Toolkit,
  req: ToolDefinition["requirements"]
) => {
  if (!req) {
    return;
  }

  toolkit.requirements.secrets = mergeSecrets(
    req.secrets ?? [],
    toolkit.requirements.secrets ?? []
  );
  toolkit.requirements.authorization = mergeAuthorizations(
    req.authorization,
    toolkit.requirements.authorization ?? []
  );

  const providerId = req.authorization?.provider_id;
  const oauthScopes = req.authorization?.oauth2?.scopes ?? [];
  if (providerId && oauthScopes.length) {
    toolkit.requirements.scopes = mergeScopes(
      providerId,
      oauthScopes,
      toolkit.requirements.scopes ?? {}
    );
  }

  toolkit.requirements.met = !!(toolkit.requirements.met && (req.met ?? true));
};

export const groupToolsIntoToolkits = (
  tools: ToolDefinition[]
): Map<string, Toolkit> => {
  const toolkits = new Map<string, Toolkit>();

  for (const tool of tools) {
    const toolkitName = tool.toolkit.name;
    let currentToolkit = toolkits.get(toolkitName);

    if (!currentToolkit) {
      currentToolkit = {
        ...tool.toolkit,
        tools: [],
        requirements: {
          met: true,
          secrets: [],
          authorization: [],
          scopes: {},
        },
      };
      toolkits.set(toolkitName, currentToolkit);
    }

    currentToolkit.tools.push(tool);

    if (tool.requirements) {
      applyToolRequirements(currentToolkit, tool.requirements);
    }
  }

  return toolkits;
};

const computeRequirementsSummary = (
  requirements: Toolkit["requirements"]
): ProcessedToolkit["requirementsSummary"] => {
  const secrets = requirements?.secrets?.length
    ? {
        configured: requirements.secrets
          .filter((secret) => secret.met === true)
          .map((secret) => secret.key),
        pending: requirements.secrets
          .filter((secret) => secret.met === false)
          .map((secret) => secret.key),
      }
    : undefined;

  const oauth = requirements?.authorization?.length
    ? {
        providers: requirements.authorization
          .filter((auth) => auth.provider_id || auth.id)
          .map((auth) => ({
            id: auth.provider_id || auth.id || "",
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            status: auth.status as "active" | "inactive",
            scopes: auth.oauth2?.scopes || [],
          })),
      }
    : undefined;

  return { secrets, oauth };
};

/**
 * Build a ToolkitCollection from pre-fetched tools data without making an API call.
 * Groups tools by toolkit and creates pre-computed search indices.
 */
export const buildToolkitCollection = (
  rawTools: ToolDefinition[]
): ToolkitCollection => {
  const rawToolkits = groupToolsIntoToolkits(rawTools);
  const toolkits: ProcessedToolkit[] = [];
  const tools: ProcessedTool[] = [];
  const toolkitMap = new Map<string, ProcessedToolkit>();
  const toolToToolkitMap = new Map<string, string>();
  const searchIndex = {
    toolkitsByName: new Map<string, ProcessedToolkit>(),
    toolsByName: new Map<string, ProcessedTool>(),
  };

  for (const [name, tk] of rawToolkits.entries()) {
    const tkSearch = normalizeText([
      tk.name,
      tk.description ?? "",
      tk.version ?? "",
    ]);

    const requirementsSummary = computeRequirementsSummary(tk.requirements);

    const processedToolkit: ProcessedToolkit = {
      ...tk,
      searchableText: tkSearch,
      requirementsSummary,
      metadata: findToolkitByName(tk.name),
    };
    toolkits.push(processedToolkit);
    toolkitMap.set(name, processedToolkit);
    searchIndex.toolkitsByName.set(name.toLowerCase(), processedToolkit);

    for (const t of tk.tools) {
      const toolSearch = normalizeText([
        t.name,
        t.fully_qualified_name,
        t.description ?? "",
        tk.name,
        tk.description ?? "",
      ]);

      const processedTool: ProcessedTool = {
        ...t,
        searchableText: toolSearch,
      };
      tools.push(processedTool);
      toolToToolkitMap.set(t.fully_qualified_name, name);
      searchIndex.toolsByName.set(
        t.fully_qualified_name.toLowerCase(),
        processedTool
      );
    }
  }

  return {
    toolkits,
    tools,
    toolkitMap,
    toolToToolkitMap,
    searchIndex,
  };
};
